import React, {useEffect, useRef, useState} from "react";
import {
    Box,
    Button,
    CardActions,
    CardContent,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {fetchCurrencies, selectCurrencies} from "../../store/currencySlice";
import {useDispatch, useSelector} from "react-redux";
import {deleteWallet, saveWallet, selectGroups} from "../../store/walletSlice";
import {selectUserSettings} from "../../store/userSettingsSlice";
import WalletIcon from "../layout/WalletIcon";
import {Clear} from "@mui/icons-material";
import WalletBalance from "../layout/WalletBalance";
import EditSetBalanceForm from "./EditSetBalanceForm";
import BalanceNumericFormat from "../utils/BalanceNumericFormat";
import ViewAndEditFormLayout from "./ViewAndEditFormLayout";
import {useTranslation} from "react-i18next";


const EditWalletForm = ({wallet, onCancel, resetTrigger}) => {
    const {t} = useTranslation();
    const DEFAULT_ERROR = {name: null, image: null, type: null, balance: null, currency: null};


    const dispatch = useDispatch();
    const userSettings = useSelector(selectUserSettings);
    const defaultCurrencyId = userSettings?.defaultCurrencyId || null;
    const [setBalanceTransaction, setSetBalanceTransaction] = useState(null);
    const [name, setName] = useState(wallet.name);
    const [type, setType] = useState(wallet.type ? wallet.type : 'WALLET');
    const [groupId, setGroupId] = useState(wallet.walletGroup);
    const [imageId, setImageId] = useState(wallet.image ? wallet.image.id : 1);
    const [balance, setBalance] = useState(wallet.balance);
    const [currencyId, setCurrencyId] = useState(wallet.currency || (wallet.id ? null : defaultCurrencyId));
    const [errors, setErrors] = useState(DEFAULT_ERROR);
    const currencies = useSelector(state => selectCurrencies(state));
    const groups = useSelector(state => selectGroups(state));
    const [childResetTrigger, setResetTrigger] = useState(Date.now());
    const balanceInputRef = useRef(null);

    useEffect(() => {
        setName(wallet.name);
        setCurrencyId(wallet.currency || (wallet.id ? null : defaultCurrencyId));
        setBalance(wallet.balance);
        setType(wallet.type ? wallet.type : 'WALLET');
        setGroupId(wallet.walletGroup);

        setErrors(DEFAULT_ERROR)
        setSetBalanceTransaction(null)

    }, [wallet]);

    useEffect(() => {
        if (!wallet.id && !currencyId && defaultCurrencyId) {
            setCurrencyId(defaultCurrencyId);
        }
    }, [defaultCurrencyId]);

    useEffect(() => {
        dispatch(fetchCurrencies())
    }, []);

    const onSave = () => {
        if (validateForm()) {
            const result = wallet.id ? "view" : "close";
            return new Promise((resolve) => {
                dispatch(
                    saveWallet(
                        {
                            ...wallet,
                            name: name,
                            walletType: type,
                            walletGroup: groupId ? groupId : null,
                            image: { id: imageId },
                            balance: balance,
                            currency: currencyId ? currencyId : null
                        },
                        () => resolve(result)
                    )
                );
            });
        }
    }

    const onNameChange = (event) => {
        setName(event.target.value);
        setErrors({...errors, name: null});
    }

    const onTypeChange = (event) => {
        setType(event.target.value);
        setErrors({...errors, type: null});
    }

    const onImageChange = (event) => {
        setImageId(event.target.value)
        setErrors({...errors, image: null});
    }

    const onCurrencyChange = (event) => {
        setCurrencyId(event.target.value)
        setErrors({...errors, currency: null});
    }

    const onBalanceChange = (event) => {
        setBalance(event.target.value)
        setErrors({...errors, balance: null});
    }

    const onBalanceFocus = () => {
        const input = balanceInputRef.current;
        if (input && typeof input.select === 'function') {
            setTimeout(() => input.select(), 0);
        }
    }

    const validateForm = () => {
        const isBalanceRequired = !wallet.id;
        const newErrors = {
            name: !name.trim() ? "Name is required" : null,
            image: !imageId ? "Image selection is required" : null,
            type: !type ? "Type is not selected" : null,
            balance: isBalanceRequired && (balance === null || balance === undefined || `${balance}`.trim() === "")
                ? "Balance is required"
                : null,
            currency: !currencyId ? "Currency is not selected" : null
        }
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    }

    const onDelete = (event) => {
        dispatch(deleteWallet(wallet, () => onCancel()))
    }

    const onTransactions = (event) => {}
    const onNewTransaction = (event) => {}
    const onSetBalance = (event) => {
        setResetTrigger(new Date())
        setSetBalanceTransaction({date: new Date(), wallet: wallet.id, value: wallet.balance})
    }

    const getWalletTypeName = (type) => {
        return type === 'DEPOSIT' ? t("forms.wallet.walletTypes.deposit") :
            type === 'CREDIT' ? t("forms.wallet.walletTypes.credit") :
            type === 'DEBT' ? t("forms.wallet.walletTypes.debt") :
            t("forms.wallet.walletTypes.wallet");
    }

    const viewContent = (
        <>
            <CardContent>
                <Box
                    sx={{
                        width: '93%',
                        backgroundColor: '#f5f5f5',
                        border: 1,
                        borderColor: 'primary.light',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography color="primary.main" variant="h6">{t("common.labels.balance")}</Typography>
                    <Typography color="primary.main" variant="h4" sx={{ fontWeight: 'bold' }}>
                        <WalletBalance wallet={wallet} />
                    </Typography>
                </Box>
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Button variant="outlined" sx={{ height: '50px', lineHeight: 1.2 }} onClick={onCancel}>
                    {t("common.buttons.close")}
                </Button>
                <Button variant="outlined" sx={{ height: '50px', lineHeight: 1.2 }} onClick={onTransactions}>{t("common.buttons.transactions")}</Button>
                {/*<Button variant="contained" sx={{ height: '50px', lineHeight: 1.2 }} onClick={onNewTransaction}>New transaction</Button>*/}
                <Button variant="contained" sx={{ height: '50px', lineHeight: 1.2 }} onClick={onSetBalance}>{t("common.buttons.setBalance")}</Button>
            </CardActions>
        </>
    );

    const editContent = ({ onCancel: handleCancel, onSave: handleSaveLayout }) => (
        <>
            <CardContent>
                <Box sx={{ m: 2 }}>
                    <TextField
                        id="name"
                        value={name}
                        onChange={onNameChange}
                        label={t("common.labels.name")}
                        variant="standard"
                        sx={{ width: "50%" }}
                        error={errors.name}
                        helperText={errors.name}
                    />
                </Box>
                <Box sx={{ m: 2 }}>
                    <TextField
                        label={t("common.labels.type")}
                        variant="standard"
                        value={type}
                        onChange={onTypeChange}
                        sx={{ width: "50%" }}
                        select>
                        <MenuItem value={'WALLET'}>{getWalletTypeName('WALLET')}</MenuItem>
                        <MenuItem value={'DEPOSIT'}>{getWalletTypeName('DEPOSIT')}</MenuItem>
                        <MenuItem value={'CREDIT'}>{getWalletTypeName('CREDIT')}</MenuItem>
                        <MenuItem value={'DEBT'}>{getWalletTypeName('DEBT')}</MenuItem>
                    </TextField>
                    <FormControl sx={{ width: "40%", paddingLeft: "12px" }}>
                        <InputLabel id="wallet-currency-select-label">{t("common.labels.image")}</InputLabel>
                        <Select
                            value={imageId}
                            variant="standard"
                            labelId="wallet-currency-select-labe"
                            onChange={onImageChange}
                            renderValue={val => (
                                <Box sx={{ position: "relative", height: 10 }}>
                                    {imageId ? (
                                        <WalletIcon image={{ id: imageId }} sx={{ position: "absolute", top: -7, left: -1 }} fontSize="large" />
                                    ) : (
                                        ""
                                    )}
                                </Box>
                            )}
                        >
                            {/*<Box>*/}
                            <MenuItem value={1} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 1 }} /></MenuItem>
                            <MenuItem value={2} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 2 }} /></MenuItem>
                            <MenuItem value={3} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 3 }} /></MenuItem>
                            <MenuItem value={4} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 4 }} /></MenuItem><br />
                            {/*</Box>*/}
                            {/*<Box>*/}
                            <MenuItem value={5} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 5 }} /></MenuItem>
                            <MenuItem value={6} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 6 }} /></MenuItem>
                            <MenuItem value={7} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 7 }} /></MenuItem>
                            <MenuItem value={8} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 8 }} /></MenuItem><br />
                            {/*</Box>*/}
                            {/*<Box>*/}
                            <MenuItem value={9} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 9 }} /></MenuItem>
                            <MenuItem value={10} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 10 }} /></MenuItem>
                            <MenuItem value={11} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 11 }} /></MenuItem>
                            <MenuItem value={12} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 12 }} /></MenuItem><br />
                            {/*</Box>*/}
                            {/*<Box>*/}
                            <MenuItem value={13} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 13 }} /></MenuItem>
                            <MenuItem value={14} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 14 }} /></MenuItem>
                            <MenuItem value={15} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 15 }} /></MenuItem>
                            <MenuItem value={16} sx={{ display: "inline-block" }}><WalletIcon image={{ id: 16 }} /></MenuItem>
                            {/*</Box>*/}
                        </Select>
                        {errors.image && <Typography variant="caption" color="error">{errors.image}</Typography>}
                    </FormControl>
                </Box>
                <Box sx={{ m: 2 }}>
                    <TextField
                        disabled={!!wallet.id}
                        label={t("common.labels.balance")}
                        variant="standard"
                        sx={{ width: "50%" }}
                        value={balance || ""}
                        onChange={onBalanceChange}
                        error={errors.balance}
                        helperText={errors.balance}
                        inputRef={balanceInputRef}
                        InputProps={{
                            inputComponent: BalanceNumericFormat,
                            readOnly: !!wallet.id,
                        }}
                        inputProps={{
                            onFocus: onBalanceFocus,
                            onClick: onBalanceFocus,
                        }}
                    />
                    <FormControl sx={{ width: "40%", paddingLeft: "12px" }}>
                        <InputLabel id="wallet-currency-select-label"
                                    sx={{ color: errors.currency ? 'error.main' : undefined }}>{t("common.labels.currency")}</InputLabel>
                        <Select
                            value={currencyId}
                            variant="standard"
                            labelId="wallet-currency-select-labe"
                            onChange={onCurrencyChange}
                            renderValue={(selectedId) => {
                                const selectedCurrency = currencies.find(c => c.id === selectedId);
                                return selectedCurrency ? selectedCurrency.shortName : '';
                            }}
                        >
                            {currencies && currencies.map(eachCurrency =>
                                <MenuItem key={eachCurrency.id} value={eachCurrency.id}>{eachCurrency.name}({eachCurrency.shortName})</MenuItem>
                            )}
                        </Select>
                        {errors.currency && <Typography variant="caption" color="error">{errors.currency}</Typography>}
                    </FormControl>
                </Box>

                {type !== 'DEBT' && (
                    <Box sx={{ m: 2 }}>
                        <TextField
                            label={t("common.labels.group")}
                            variant="standard"
                            value={groupId}
                            onChange={(event) => setGroupId(event.target.value)}
                            sx={{ width: "50%" }}
                            select
                            SelectProps={{
                                endAdornment: groupId && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setGroupId('');
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                right: '24px',
                                                zIndex: 1,
                                            }}
                                            edge="end"
                                        >
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        >
                            {groups && groups.map(group => <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>)}
                        </TextField>
                    </Box>
                )}
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={handleCancel}>{t("common.buttons.cancel")}</Button>
                        <Button variant="contained" onClick={handleSaveLayout}>{t("common.buttons.save")}</Button>
                    </Box>
                </Box>
            </CardActions>
        </>
    );

    return wallet ? (
        <>
            {setBalanceTransaction ? (
                <EditSetBalanceForm
                    setBalance={setBalanceTransaction}
                    onCancel={() => {
                        setSetBalanceTransaction(null);
                    }}
                    resetTrigger={childResetTrigger}
                />
            ) : (
                <ViewAndEditFormLayout
                    editTitle={wallet.id ? t("forms.wallet.editTitle") : `${t("forms.wallet.createTitle")} ${getWalletTypeName(type)}`}
                    viewTitle={`${getWalletTypeName(type)}: ${name}`}
                    initialEdit={!wallet.id}
                    onCancel={onCancel}
                    onSave={onSave}
                    onDelete={wallet.id ? onDelete : undefined}
                    viewContent={wallet.id ? viewContent : null}
                    editContent={editContent}
                />
            )}
        </>
    ) : <></>
}



export default EditWalletForm